import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry, themeQuartz } from 'ag-grid-community';
import { Link, useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import MenuIcon from '@mui/icons-material/Menu';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { InputDialog } from '../../components/InputDialog';
import { exportToExcel } from "../../utils/exportUtils";
import api from '../../services/api';

ModuleRegistry.registerModules([AllCommunityModule]);

const theme = themeQuartz.withParams({
  columnBorder: true,
  oddRowBackgroundColor: '#f9f9f9',
  cellFocusBorderColor: 'transparent',
  fontSize: 11.5,
  rowHeight: 32,
  headerHeight: 34,
  filterHeight: 30,
  cellHorizontalPadding: 4,
});

function ApplicantListAG() {
  const [rowData, setRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, rowData }
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const fetchCalled = useRef(false);

  const [isDocumentSelectorOpen, setIsDocumentSelectorOpen] = useState(false);
  const [showInputDialog, setShowInputDialog] = useState(false);
  const [selectionCount, setSelectionCount] = useState(0);
  const [isInactive] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [statusAnchor, setStatusAnchor] = useState(null);
  const menuCloseRef = useRef(false);
  const [actionsAnchor, setActionsAnchor] = useState(null);
  
    // Refs
    const gridWrapperRef = useRef(null);
    const contextMenuRef = useRef(null);
    const dataGridRef = useRef(null);
    const focusedRowKeyRef = useRef(null);
    const selectedIdsRef = useRef(new Set());
    const prioritizeSelectedRef = useRef(false); // Only for passport search
    const passportSearchIdsRef = useRef(new Set()); // Only passport search results
    const navigate = useNavigate();
  

  // Define helper function for date formatting
  const formatDateInline = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d)) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const contextMenuItems = useMemo(() => [
    { text: 'Search by Passport Numbers', icon: '🔍' },
    { text: 'Print', icon: '🖨️' },
    { text: 'Print Summary', icon: '🖨️' },
    { text: 'Check Visa Delegation (Wekala)', icon: '📤' },
    { text: 'Export', icon: '📁', items: [
      { text: 'Export for Tasheer', icon: '📄' },
      { text: 'Export Selected Applicants', icon: '📄' },
      { text: 'Export All', icon: '📄' },
    ]},
    { text: 'Print Contract', icon: '🖨️' },
    { text: 'Print CV', icon: '🖨️' },
    { text: 'Print Selected Documents', icon: '🖨️' },
    { text: 'Forensic Request Letter', icon: '🖨️' },
    { text: 'Ticket Request List', icon: '🖨️' },
    { text: 'Print Beneficiary Declaration', icon: '🖨️' },
    { text: 'Commission Inquiry', icon: '🖨️' },
    { text: 'Change Applicant Status', icon: '✏️' },
    { text: 'Change Other Status', icon: '✏️', items: [
      { text: 'Medical Status', icon: '🏥' },
      { text: 'COC Status', icon: '📜' },
      { text: 'Tasheer Status', icon: '📄' },
      { text: 'LMIS Issue Date', icon: '📅' },
    ]},
    { text: 'Add Complaint', icon: '➕' },
    { text: 'Add Flight Detail', icon: '➕' },
    { text: 'Move Applicants', icon: '📤' },
    { text: 'Send Applicant', icon: '📤' },
  ], []);

  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    // Approximate menu dimensions
    const menuWidth = 240;
    const menuHeight = 480;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const x = e.clientX + menuWidth > vw ? e.clientX - menuWidth : e.clientX;
    const y = e.clientY + menuHeight > vh ? Math.max(4, vh - menuHeight) : e.clientY;
    setContextMenu({ x, y });
    setOpenSubmenu(null);
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
    setOpenSubmenu(null);
  }, []);

  const getSelectedRowData = useCallback(() => {
    const selected = dataGridRef.current?.api?.getSelectedRows();
    if (selected?.length > 0) return selected[0];
    // Fall back to focused row when nothing is checked
    const focusedCell = dataGridRef.current?.api?.getFocusedCell();
    if (focusedCell != null) {
      return dataGridRef.current.api.getDisplayedRowAtIndex(focusedCell.rowIndex)?.data ?? null;
    }
    return null;
  }, []);

  const handleExport = useCallback(async (ids, type) => {
    try {
      setIsExporting(true);
      let url, body;
      if (type === 'all') {
        url = '/Applicants/export-all';
        body = null;
      } else if (type === 'tasheer') {
        url = '/Applicants/tasheer';
        body = ids;
      } else {
        url = '/Applicants/export-selected';
        body = ids;
      }

      const response = await api.post(url, body, { responseType: 'blob' });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `Applicants_Export_${Date.now()}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
      setSnackbar({ message: 'Export completed successfully', severity: 'success' });
    } catch (err) {
      console.error('Export failed:', err);
      setSnackbar({ message: 'Export failed. Please try again.', severity: 'error' });
    } finally {
      setIsExporting(false);
    }
  }, []);

  const handleAction = useCallback((action) => {
    const selected = dataGridRef.current?.api?.getSelectedRows() ?? [];
    const ids = selected.map(r => r.id);
    const first = selected[0];
    switch (action) {
      case 'enjaz': navigate(`/reports/applicantreport/${first?.id}`); break;
      case 'summary': navigate('/reports/applicantSummary', { state: { selectedIds: ids } }); break;
      case 'cv': navigate('/reports/print_cv', { state: { selectedIds: ids } }); break;
      case 'documents': setIsDocumentSelectorOpen(true); break;
      case 'ticket-request': navigate('/reports/ticket-request', { state: { selectedIds: ids } }); break;
      case 'beneficiary-declaration': navigate('/reports/print-benificary', { state: { selectedIds: ids } }); break;
      case 'export-tasheer': handleExport(ids, 'tasheer'); break;
      case 'commissionInquiry': navigate('/reports/commissionInquiry', { state: { selectedIds: ids } }); break;
      case 'change-status': navigate('/applicants/changeStatus', { state: { selectedIds: ids } }); break;
      case 'change-medical-status': navigate('/applicants/medicalStatus', { state: { selectedIds: ids } }); break;
      case 'change-coc-status': navigate('/applicants/cocStatus', { state: { selectedIds: ids } }); break;
      case 'change-lmis-issue-date': navigate('/applicants/changeLmisDate', { state: { selectedIds: ids } }); break;
      case 'change-musaned-status': navigate('/applicants/tasheerStatus', { state: { selectedIds: ids } }); break;
      case 'add-complain': navigate(`/complains/create/${first?.id}`); break;
      case 'add-flight': navigate('/flights/create', { state: { selectedIds: ids } }); break;
      default: break;
    }
  }, [navigate, handleExport]);

  const handleOpenInputDialog = useCallback(() => {
    setShowInputDialog(true);
    closeContextMenu();
  }, [closeContextMenu]);

  const handlePassportSearch = useCallback((input) => {
    setShowInputDialog(false);
    if (!input) return;

    // Parse passport numbers — split by newline, comma, or space
    const passports = input
      .split(/[\n,\s]+/)
      .map(p => p.trim().toUpperCase())
      .filter(Boolean);

    if (passports.length === 0) return;

    const api = dataGridRef.current?.api;
    if (!api) return;

    // Add new passports to the accumulated set
    passports.forEach(p => passportSearchIdsRef.current.add(p));
    const allPassports = Array.from(passportSearchIdsRef.current);

    // Select all matching rows (new + previously searched)
    api.forEachNode((node) => {
      if (allPassports.includes((node.data.passportNo || '').toUpperCase())) {
        node.setSelected(true);
      }
    });

    // New passports go first, then previously searched, then manually selected, then rest
    api.setGridOption('postSortRows', (params) => {
      params.nodes.sort((a, b) => {
        const aPassport = (a.data?.passportNo || '').toUpperCase();
        const bPassport = (b.data?.passportNo || '').toUpperCase();
        const aNew = passports.includes(aPassport) ? 0
          : allPassports.includes(aPassport) ? 1
          : selectedIdsRef.current.has(a.data?.id) ? 2
          : 3;
        const bNew = passports.includes(bPassport) ? 0
          : allPassports.includes(bPassport) ? 1
          : selectedIdsRef.current.has(b.data?.id) ? 2
          : 3;
        return aNew - bNew;
      });
    });
    api.refreshClientSideRowModel('sort');
  }, []);


  const executeContextMenuAction = useCallback((actionText, idsToUse, selectedRowData) => {
    switch (actionText) {
      case 'Print': navigate(`/reports/applicantreport/${selectedRowData.id}`); break;
      case 'Print Summary': navigate('/reports/applicantSummary', { state: { selectedIds: idsToUse } }); break;
      case 'Check Visa Delegation (Wekala)':
        (async () => {
          try {
            const textToCopy = `VNo(${selectedRowData?.visaNo}),SId(${selectedRowData?.sponsorID})`;
            await navigator.clipboard.writeText(textToCopy);
            window.open('https://visa.mofa.gov.sa/Enjaz/GetVisaInformation/Person', '_blank');
          } catch (err) { console.error('Clipboard error:', err); }
        })();
        break;
      case 'Export for Tasheer': handleExport(idsToUse, 'tasheer'); break;
      case 'Export Selected Applicants': handleExport(idsToUse, 'selected'); break;
      case 'Export All': handleExport([], 'all'); break;
      case 'Print Contract': navigate('/reports/print_contract', { state: { selectedIds: idsToUse } }); break;
      case 'Print CV': navigate('/reports/print_cv', { state: { selectedIds: idsToUse } }); break;
      case 'Print Selected Documents': setIsDocumentSelectorOpen(true); break;
      case 'Forensic Request Letter': navigate('/reports/forensic-request', { state: { selectedIds: idsToUse } }); break;
      case 'Ticket Request List': navigate('/reports/ticket-request', { state: { selectedIds: idsToUse } }); break;
      case 'Print Beneficiary Declaration': navigate('/reports/print-benificary', { state: { selectedIds: idsToUse } }); break;
      case 'Commission Inquiry': navigate('/reports/commissionInquiry', { state: { selectedIds: idsToUse } }); break;
      case 'Send Applicant': navigate(`/applicants/sendApplicant/${selectedRowData.id}`); break;
      case 'Move Applicants': navigate('/applicants/moveApplicants', { state: { selectedIds: idsToUse } }); break;
      case 'Change Applicant Status': navigate('/applicants/changeStatus', { state: { selectedIds: idsToUse } }); break;
      case 'Medical Status': navigate('/applicants/medicalStatus', { state: { selectedIds: idsToUse } }); break;
      case 'COC Status': navigate('/applicants/cocStatus', { state: { selectedIds: idsToUse } }); break;
      case 'LMIS Issue Date': navigate('/applicants/changeLmisDate', { state: { selectedIds: idsToUse } }); break;
      case 'Tasheer Status': navigate('/applicants/tasheerStatus', { state: { selectedIds: idsToUse } }); break;
      case 'Add Complaint': navigate(`/complains/create/${selectedRowData.id}`); break;
      case 'Add Flight Detail': navigate('/flights/create', { state: { selectedIds: idsToUse } }); break;
      case 'Search by Passport Numbers': handleOpenInputDialog(); break;
      default: break;
    }
  }, [navigate, handleExport, handleOpenInputDialog]);

  const handleContextMenuClick = useCallback((actionText) => {
    const selectedRowData = getSelectedRowData();
    if (!selectedRowData) return;
    const idsArray = Array.from(selectedIdsRef.current);
    const idsToUse = idsArray.length > 0 ? idsArray : [selectedRowData.id];
    executeContextMenuAction(actionText, idsToUse, selectedRowData);
    closeContextMenu();
  }, [getSelectedRowData, executeContextMenuAction, closeContextMenu]);
  // Column definitions - floatingFilter/sortable/filter handled by defaultColDef
  const columnDefs = useMemo(() => [
    {
      headerName: '', width: 36, filter: false, sortable: false, floatingFilter: false,
      checkboxSelection: true, headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      headerCheckboxSelectionCurrentPageOnly: true, // Optional: if using pagination
      headerClass: 'ag-header-cell-center',
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
      onCellClicked: (params) => params.node.setSelected(!params.node.isSelected()),
    },
    {
      headerName: '...', width: 36, filter: false, sortable: false, floatingFilter: false,
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
      cellRenderer: (params) => (
        <Link to={`/applicants/edit/${params.data.id}`}>
          <EditIcon fontSize="small" style={{ color: '#1a73e8' }} />
        </Link>
      ),
    },
    { field: 'applicationDate', headerName: 'Date', width: 73 },
    { field: 'applicationNo', headerName: 'App #', width: 80 },
    { field: 'fullName', headerName: 'Full Name', width: 165 },
    { field: 'passportNo', headerName: 'Passport #', width: 75 },
    { field: 'age', headerName: 'Age', width: 50, type: 'numericColumn', filter: 'agNumberColumnFilter', filterParams: { defaultOption: 'equals' } },
    { field: 'experienceInAbroad', headerName: 'Exp', width: 50, type: 'numericColumn', filter: 'agNumberColumnFilter', filterParams: { defaultOption: 'equals' } },
    { field: 'workingCountry', headerName: 'Works In', width: 70 },
    { field: 'sponsorName', headerName: 'Sponsor', width: 100 },
    { field: 'sponsorID', headerName: 'Sponsor ID', width: 80 },
    { field: 'visaNo', headerName: 'Visa #', width: 80 },
    { field: 'agent', headerName: 'Agent', width: 85 },
    {
      field: 'status', headerName: 'Status', width: 75/*,
      cellStyle: (params) => {
        if (params.value === 'TRAVELED') return { color: 'green', fontWeight: 'bold' };
        if (params.value === 'Rejected') return { color: 'red' };
        if (params.value === 'Pending') return { color: 'orange' };
        return null;
      }*/
    },
    { field: 'statusDate', headerName: 'Status Date', width: 73 },
    { field: 'brokerName', headerName: '2nd.Con', width: 70 },
    { field: 'cocCenter', headerName: 'COC', width: 45 },
    { field: 'medicalOnlineStatus', headerName: 'MED', width: 45 },
    { field: 'medicalDays', headerName: 'M.Days', width: 55 },
    //{
      //field: 'inMusaned', headerName: 'TASH', width: 50
      //,cellRenderer: (params) => params.value=='true' ? 'Yes ✓' : 'No ✗'
    //},
    {
        headerName: 'TASH',
        width: 50,
        valueGetter: (params) => {
            return params.data.inMusaned === 'true' || params.data.inMusaned === 'Y' || params.data.inMusaned === 'Yes' ? '✓ Yes' : '✗ No';
        },
        filter: 'agTextColumnFilter',
        // This ensures filtering works on the display text
        filterValueGetter: (params) => {
            return params.data.inMusaned === 'true' || params.data.inMusaned === 'Y' || params.data.inMusaned === 'Yes' ? 'Yes' : 'No';
        }
        },
    { field: 'bioEntryDate', headerName: 'LMIS', width: 73 },
    { field: 'laborID', headerName: 'Labor ID', width: 90 },
    { field: 'signedDays', headerName: 'Days', width: 73, type: 'numericColumn', filter: 'agNumberColumnFilter' },
    { field: 'createdBy', headerName: 'User', width: 70 },
    { field: 'religion', headerName: 'Religion', width: 70 },    
    { field: 'laborID', headerName: 'Labor-ID', width: 70 },
    { field: 'memo', headerName: 'Remark', width: 200 }
  ], []);

  useEffect(() => {
    if (fetchCalled.current) return;
    fetchCalled.current = true;

    setIsLoading(true);

    api.get('/Applicants')
      .then((res) => {
        const transformedData = res.data.map(item => ({
          ...item,
          applicationDate: formatDateInline(item.applicationDate),
          statusDate: formatDateInline(item.statusDate),
          bioEntryDate: formatDateInline(item.bioEntryDate),
          applicationNo: item.applicationNo || '',
          fullName: item.fullName || '',
          passportNo: item.passportNo || '',
          religion: item.religion || '',
          experienceInAbroad: item.experienceInAbroad || '',
          workingCountry: item.workingCountry || '',
          sponsorName: item.sponsorName || '',
          sponsorID: item.sponsorID || '',
          visaNo: item.visaNo || '',
          agent: item.agent || '',
          brokerName: item.brokerName || '',
          cocCenter: item.cocCenter || '',
          medicalOnlineStatus: item.medicalOnlineStatus || '',
          status: item.status || '',
          laborID: item.laborID || '',
          inMusaned: item.inMusaned || '',
          createdBy: item.createdBy || '',
          memo: item.memo || ''
        }));
        
        setRowData(transformedData);
      })
      .catch((err) => console.error('Error fetching applicants:', err))
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div>Loading applicants...</div>
      </div>
    );
  }

  return (
    <div
      style={{ height: 'calc(100vh - 100px)', width: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}
      onContextMenu={handleContextMenu}
      onClick={closeContextMenu}
    >
      {/* Toolbar */}
      <div style={toolbarStyle} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-center es-label-title" style={{ gap: '10px' }}>
          <strong>{isInactive ? 'INACTIVE APPLICANTS' : 'APPLICANTS'}</strong>
          <span style={{ color: '#1976d2', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
            Selected: <span className="selection-count-display-inline">{selectionCount}</span>
            {prioritizeSelectedRef.current && (
              <button
                onClick={() => {
                  prioritizeSelectedRef.current = false;
                  passportSearchIdsRef.current.clear();
                  dataGridRef.current?.api?.setGridOption('postSortRows', null);
                  dataGridRef.current?.api?.refreshClientSideRowModel('sort');
                }}
                style={{ background: 'none', border: '1px solid #1976d2', borderRadius: 3, color: '#1976d2', fontSize: 11, padding: '2px 6px', cursor: 'pointer' }}
                title="Reset to normal order"
              >Reset</button>
            )}
          </span>

          <Button className="es-toolbar-button" size="small" variant="text" sx={{ color: 'black' }} startIcon={<MenuIcon fontSize="small" />} onClick={(e) => { const t = e.currentTarget; setStatusAnchor(null); setTimeout(() => setActionsAnchor(t), 0); }}>Actions ▾</Button>
          <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={() => setActionsAnchor(null)}>
            {[['Print Enjaz','enjaz'],['Print Summary','summary'],['Print CV','cv'],['Print Documents','documents'],['Ticket Request List','ticket-request'],['Print Beneficiary','beneficiary-declaration'],['Export for Tasheer','export-tasheer'],['Commission Inquiry','commissionInquiry']].map(([label, action]) => (
              <MenuItem key={action} dense onClick={() => handleAction(action)}>{label}</MenuItem>
            ))}
          </Menu>

          <Button size="small" variant="text" sx={{ color: 'black' }} startIcon={<MenuIcon fontSize="small" />} onClick={(e) => { const t = e.currentTarget; setActionsAnchor(null); setTimeout(() => setStatusAnchor(t), 0); }}>Status ▾</Button>
          <Menu anchorEl={statusAnchor} open={Boolean(statusAnchor)} onClose={() => setStatusAnchor(null)}>
            {[['Change Applicant Status','change-status'],['Change Medical Status','change-medical-status'],['Change COC Status','change-coc-status'],['Change LMIS Issue Date','change-lmis-issue-date'],['Change Tasheer Status','change-musaned-status']].map(([label, action]) => (
              <MenuItem key={action} dense onClick={() => handleAction(action)}>{label}</MenuItem>
            ))}
          </Menu>

          <Button className="es-toolbar-button" size="small" variant="text" sx={{ color: 'black' }} onClick={() => handleAction('add-complain')}>+ Complain</Button>
          <Button size="small" variant="text" sx={{ color: 'black' }} onClick={() => handleAction('add-flight')}>+ Flight</Button>
        </div>
      </div>

      {/* Grid */}
      <div style={{ flex: 1, '--ag-cell-widget-spacing': '0px' }}>
        <style>{`
          .ag-header-cell-center { display: flex !important; align-items: center !important; justify-content: center !important; }
          .ag-header-cell-center .ag-header-cell-comp-wrapper { display: none; }
          .ag-cell-wrapper.ag-row-group { justify-content: flex-start; }
        `}</style>
        <AgGridReact
        ref={dataGridRef}
        theme={theme}
        rowData={rowData}
        columnDefs={columnDefs}
        rowBuffer={100}
        rowModelType="clientSide"
        defaultColDef={{
          sortable: true,
          filter: true,
          floatingFilter: true,
          resizable: true,
          editable: false,
          filterParams: { defaultOption: 'startsWith' }
        }}
        enableCellTextSelection={true}
        rowSelection="multiple"
        suppressRowClickSelection={true}
        onSelectionChanged={(e) => {
          const selected = e.api.getSelectedRows();
          selectedIdsRef.current = new Set(selected.map(r => r.id));
          setSelectionCount(selected.length);
        }}
        onRowClicked={(event) => {
          console.log('Row clicked:', event.data);
        }}
      />
      </div>

      <InputDialog
        show={showInputDialog}
        onClose={handlePassportSearch}
        title="Search by Passport Numbers"
        label="Enter passport numbers (one per line or comma separated)"
      />

      {contextMenu && (
        <ul
          style={{ ...menuStyle, top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenuItems.map((item, i) => (
            <li
              key={i}
              style={{ ...menuItemStyle, background: openSubmenu === i ? '#f0f0f0' : 'transparent' }}
              onMouseEnter={() => setOpenSubmenu(i)}
              onClick={() => { if (!item.items) { handleContextMenuClick(item.text); } }}
            >
              <span>{item.icon} {item.text}</span>
              {item.items && <span style={{ marginLeft: 'auto', fontSize: 10 }}>▶</span>}
              {item.items && openSubmenu === i && (
                <ul style={{ ...menuStyle, position: 'absolute', top: 0, left: '100%' }}>
                  {item.items.map((sub, j) => (
                    <li
                      key={j}
                      style={menuItemStyle}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      onClick={(e) => { e.stopPropagation(); handleContextMenuClick(sub.text); }}
                    >
                      {sub.icon} {sub.text}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ position: 'absolute', top: '50% !important', left: '50% !important', transform: 'translate(-50%, -50%)' }}
      >
        <Alert onClose={() => setSnackbar(null)} severity={snackbar?.severity} sx={{ width: '100%' }}>
          {snackbar?.message}
        </Alert>
      </Snackbar>

      {isExporting && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: '#1976d2', color: '#fff', padding: '8px 20px', borderRadius: 4, fontSize: 13, zIndex: 9999 }}>
          Exporting...
        </div>
      )}
    </div>
  );
}

const toolbarStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '4px 8px',
  borderBottom: '1px solid #e0e0e0',
  background: '#fafafa',
  flexShrink: 0,
};

const menuStyle = {
  position: 'fixed',
  background: '#fff',
  border: '1px solid #ddd',
  borderRadius: 4,
  boxShadow: '2px 4px 12px rgba(0,0,0,0.15)',
  padding: '4px 0',
  margin: 0,
  listStyle: 'none',
  zIndex: 9999,
  minWidth: 220,
};

const menuItemStyle = {
  padding: '3px 12px',
  cursor: 'pointer',
  fontSize: 12,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  position: 'relative',
  whiteSpace: 'nowrap',
};

export default ApplicantListAG